import React from "react"
import { Image, Linking, Pressable, TouchableOpacity, View } from "react-native"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Body, H5, Small } from "@/components/standard/BFText"
import { Card, CardContent } from "@/components/reusables/ui/card"
import IconArrowSquareOut from "phosphor-react-native/src/icons/ArrowSquareOut"

// Import profile images
// @ts-expect-error works..
import CoryPeterson from "@/assets/images/profiles/CoryPeterson.png"
// @ts-expect-error works..
import KandiceVera from "@/assets/images/profiles/KandiceVera.png"
// @ts-expect-error works..
import MelSkinner from "@/assets/images/profiles/MelSkinner.png"
// @ts-expect-error works..
import NatalieMarshall from "@/assets/images/profiles/NatalieMarshall.png"
// @ts-expect-error works..
import TimOConnor from "@/assets/images/profiles/TimOConnor.png"

// Avatar mapping based on processor names
const AVATAR_MAPPING = {
  "Tim O'Connor": TimOConnor,
  "Kandice Vera": KandiceVera,
  "Mel Skinner": MelSkinner,
  "Natalie Marshall": NatalieMarshall,
  "Cory Petersen": CoryPeterson,
} as const

// Helper function to normalize names for matching
const normalizeName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z\s]/g, "") // Remove special characters
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim()
}

// Helper function to get avatar from name
const getAvatarFromName = (name?: string | null): any => {
  if (!name) return null

  const normalizedInputName = normalizeName(name)

  // Try to find exact match first
  const exactMatch = Object.keys(AVATAR_MAPPING).find(
    (key) => normalizeName(key) === normalizedInputName
  ) as keyof typeof AVATAR_MAPPING

  if (exactMatch) {
    return AVATAR_MAPPING[exactMatch]
  }

  // Try partial matching (first and last name)
  const inputWords = normalizedInputName.split(" ")
  const matchingKey = Object.keys(AVATAR_MAPPING).find((key) => {
    const keyWords = normalizeName(key).split(" ")
    return (
      inputWords.some((word) => keyWords.includes(word)) &&
      keyWords.some((word) => inputWords.includes(word))
    )
  }) as keyof typeof AVATAR_MAPPING

  return matchingKey ? AVATAR_MAPPING[matchingKey] : null
}

// Helper function to get initials from name
const getInitials = (name?: string | null): string => {
  if (!name) return "LP"
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

interface LoanProcessor {
  email?: string | null
  name?: string | null
  phone?: string | null
  scheduleUrl?: string | null
}

interface ProcessorCardProps {
  loanProcessor?: LoanProcessor
  title?: string
  optoutPress: () => void
  callDate?: Date
}

export const ProcessorCard: React.FC<ProcessorCardProps> = ({
  loanProcessor,
  title = "Loan Processor",
  optoutPress,
  callDate,
}) => {
  const handlePress = async () => {
    if (loanProcessor?.scheduleUrl) {
      try {
        await Linking.openURL(loanProcessor.scheduleUrl)
      } catch (error) {
        console.error("Error opening schedule URL:", error)
      }
    }
  }

  const avatar = getAvatarFromName(loanProcessor?.name)
  const initials = getInitials(loanProcessor?.name)
  const isClickable = !!loanProcessor?.scheduleUrl && !callDate

  const CardWrapper = isClickable ? TouchableOpacity : View

  return (
    <Vertical>
      <Card className="mt-3">
        <CardContent className="p-0">
          <CardWrapper
            onPress={isClickable ? handlePress : undefined}
            className="p-4"
            activeOpacity={isClickable ? 0.7 : 1}
          >
            <Horizontal className="items-center gap-4">
              {/* Avatar */}
              <View className="size-12 rounded-full overflow-hidden bg-muted items-center justify-center">
                {avatar ? (
                  <Image
                    source={avatar}
                    style={{ width: 48, height: 48 }}
                    resizeMode="cover"
                  />
                ) : (
                  <Small color="hint">{initials}</Small>
                )}
              </View>

              {/* Processor info */}
              <Vertical className="flex-1">
                <H5 color="default">
                  {loanProcessor?.name || "Your Processor"}
                </H5>
                <Small color="hint">{title}</Small>
                <Small className={"text-primary"}>
                  {callDate?.toLocaleString() || "Schedule Kickoff Call"}
                </Small>
              </Vertical>

              {/* Action Icon */}
              {loanProcessor?.scheduleUrl && !callDate && (
                <View className="pl-2">
                  <IconArrowSquareOut
                    size={20}
                    color="#676080" // hint color
                  />
                </View>
              )}
            </Horizontal>
          </CardWrapper>
        </CardContent>
      </Card>
      {!callDate && (
        <Vertical className={"mt-2 gap-1"}>
          <Body>No call needed?</Body>
          <Pressable testID={"opt-out"} onPress={optoutPress}>
            <H5 color={"primaryLight"}>Mark as complete ›</H5>
          </Pressable>
        </Vertical>
      )}
    </Vertical>
  )
}
